import { Component, Input, type OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { StaticHelpers } from '@odin/odin-core';

export type MerchantDataFormat = 'json' | 'kv' | 'text';

interface MerchantDataPair {
  key: string;
  value: string;
  /** true when the value was a nested object/array, rendered as inline JSON */
  nested: boolean;
}

/**
 * Displays the free-text data field merchants submit with a payment.
 * Merchants use it for their own order refs, invoice numbers etc. —
 * some send plain text, some key: value lines, some JSON (optionally
 * base64-encoded). Detect the format and render the friendliest view,
 * always keeping the original submission available.
 */
@Component({
  standalone: false,
  selector: 'odin-merchant-data-card',
  templateUrl: './merchant-data-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './merchant-data-card.component.scss',
})
export class MerchantDataCardComponent implements OnChanges {
  @Input({ required: true }) value: string;

  public format: MerchantDataFormat = 'text';
  public pairs: MerchantDataPair[] = [];
  public displayText: string = '';

  public get formatLabel(): string {
    switch (this.format) {
      case 'json': return 'JSON';
      case 'kv': return 'Key / value';
      default: return 'Free text';
    }
  }

  ngOnChanges(): void {
    this.parse();
  }

  private parse(): void {
    this.format = 'text';
    this.pairs = [];
    this.displayText = this.value ?? '';
    if (!this.value) return;

    // base64-wrapped JSON (the VT submits this shape)
    if (StaticHelpers.isJsonB64(this.value)) {
      const decoded = StaticHelpers.decodeJsonB64(this.value);
      if (this.tryObject(decoded)) return;
    }

    // plain JSON object
    try {
      if (this.tryObject(JSON.parse(this.value))) return;
    } catch {
      // not JSON
    }

    // key: value / key=value lines — every non-empty line must match
    const lines = this.value.split(/\r?\n/).filter((line) => line.trim() !== '');
    if (lines.length > 0 && lines.every((line) => /^[^:=]{1,60}[:=]/.test(line))) {
      this.format = 'kv';
      this.pairs = lines.map((line) => {
        const index = line.search(/[:=]/);
        return {
          key: line.slice(0, index).trim(),
          value: line.slice(index + 1).trim(),
          nested: false,
        };
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private tryObject(parsed: any): boolean {
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
    this.format = 'json';
    this.pairs = Object.keys(parsed).map((key) => {
      const raw = parsed[key];
      const nested = raw !== null && typeof raw === 'object';
      return {
        key,
        value: nested ? JSON.stringify(raw) : String(raw ?? '-'),
        nested,
      };
    });
    return true;
  }
}
